import { useState, useMemo, useEffect, memo } from 'react'
import { useNavigate } from 'react-router-dom'
import { URLS } from 'constants/urls'
import { useAppDispatch } from 'redux/hooks'
import { Confirmation } from 'ui/molecules/Confirmation'
import { useApiRequest } from 'hooks/useApiRequest'
import { setNotificationMessage } from 'redux/notifications/slice'
import { Typography, LazyImage } from '@frontend/design_system'
import { fleetApi, FleetApiTypes } from 'api'
import { Table } from 'ui/molecules/Table'
import { headItems } from '../../constants'
import { TABLE_INFO } from 'texts/vehicleDetails'
import { VEHICLES, VEHICLES_DELETE_CONFIRMATION } from 'texts/vehicles'
import { VehicleTableProps } from './types'
import { SEQUENCE_NUMBER } from 'texts/uiTexts'
import { TableTooltip } from 'ui/molecules/TableTooltip'
import styles from './styles.module.scss'
import { useCompanyId } from 'hooks/useCompanyId'
import { setPendingCarsCount } from 'redux/fleet/slice'
import { Loader } from 'ui/atoms/Loader'

const PAGE_SIZE = 100

export const VehiclesTable = memo(({ currentId }: VehicleTableProps) => {
  const [isRemoveModalOpen, setRemoveModalOpen] = useState(false)
  const [selectedCar, setSelectedCar] = useState<FleetApiTypes.Vehicle | null>(
    null
  )
  const [carList, setCarList] =
    useState<FleetApiTypes.GetVehiclesListResponse | null>(null)
  const companyId = useCompanyId()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  const deleteCarRequest = useApiRequest(
    (vehicleId) => fleetApi.deleteVehicle(vehicleId),
    undefined,
    true,
    undefined,
    false
  )

  const carListRequest = useApiRequest((companyId) =>
    fleetApi.getVehiclesList(0, PAGE_SIZE, companyId)
  )

  const getCarListData = async (companyId: string | number) => {
    const response = await carListRequest.apiRequest(companyId)
    if (response?.data) {
      setCarList(response.data)
      dispatch(setPendingCarsCount(response.data.vehicles.pageItems.length))
    }
  }

  useEffect(() => {
    if (companyId) {
      getCarListData(companyId)
    }
  }, [companyId])

  const handleRowClick = (id: string) => {
    navigate(`${URLS.FLEET}${URLS.VEHICLES}/${companyId}/${id}/edit`)
  }

  const handleDeleteCar = (id: string) => {
    const car = carList?.vehicles.pageItems.find((item) => item.uniqueId === id)
    if (car) {
      setRemoveModalOpen(true)
      setSelectedCar(car)
    }
  }

  const handleRemoveConfirm = async () => {
    if (selectedCar && companyId) {
      const response = await deleteCarRequest.apiRequest(selectedCar.uniqueId)
      if (response) {
        dispatch(
          setNotificationMessage({
            notificationMessage: VEHICLES_DELETE_CONFIRMATION.SUCCESS,
          })
        )
        getCarListData(companyId)
      }
      setRemoveModalOpen(false)
    }
  }

  const handleRemoveModalClose = () => {
    setSelectedCar(null)
    setRemoveModalOpen(false)
  }

  const formattedBodyItems = useMemo(
    () =>
      carList?.vehicles.pageItems.map((item, i) => {
        const sequenceNumber = i + 1
        return {
          items: [
            sequenceNumber,
            <div className={styles.vehicle}>
              <LazyImage src={item.image} className={styles.image} />
              <Typography name="Subtitle7">{item.name}</Typography>
            </div>,
            item.companyClass,
            item.sippCode,
            item.groupName,
            <TableTooltip
              title={item.hirePoints?.length ? item.hirePoints?.join(', ') : ''}
              onClick={() => handleRowClick(item.uniqueId)}
            />,
          ],
          onClick: () => {
            handleRowClick(item.uniqueId)
          },
          removeRowClick: () => {
            handleDeleteCar(item.uniqueId)
          },
          isActive: item.uniqueId === currentId,
        }
      }),
    [carList]
  )

  const formattedHeadItems = [SEQUENCE_NUMBER, ...headItems].map((el) => ({
    value: <Typography name="Subtitle3">{el}</Typography>,
  }))

  return (
    <>
      {isRemoveModalOpen && (
        <Confirmation
          confirmModalProps={{
            question: `${VEHICLES_DELETE_CONFIRMATION.CONFIRMATION_MODAL.QUESTION} ${selectedCar?.name}?`,
            onConfirm: handleRemoveConfirm,
            onClose: handleRemoveModalClose,
            title: `${VEHICLES.DELETE} ${selectedCar?.name} ${VEHICLES.MODEL}`,
          }}
        />
      )}
      <div className={styles['vehicle-block']}>
        <div className={styles['vehicle-header']}>
          <div className={styles['vehicle-title']}>
            <Typography name="H5">{TABLE_INFO.TITLE}</Typography>
            <Typography name="Button2">{TABLE_INFO.SUBTITLE}</Typography>
          </div>
        </div>
        <div className={styles.table}>
          {carListRequest.loading ? (
            <div className={styles.loader}>
              <Loader />
            </div>
          ) : (
            <Table
              headItems={formattedHeadItems}
              bodyItems={formattedBodyItems}
              initItemsWidth={['53', '260', '125', '90', '130', '195']}
            />
          )}
        </div>
      </div>
    </>
  )
})
